import { Box, CircularProgress, SxProps, Theme, useTheme } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { ReactNode, useCallback, useEffect } from 'react';
import { EntityOpenModeSwitcher } from 'src/components';
import { Address, Conversation } from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import {
  getNotificationEntityName,
  getNotificationOpenBehavior,
  getNotificationTargetPath,
} from 'src/graphql/entities/notification/notification-target';
import { parseNotificationCompositeKey } from 'src/graphql/entities/notification/utils';
import { AnySearchableEntity } from 'src/graphql/entities/search/use-search';
import { useSelectedRecord } from 'src/graphql/entities/search/use-selected-record';
import { RecordOpenMode } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { EntityNames } from 'src/graphql/types';
import { ClientWindow } from 'src/pages/Clients/client';
import { ConversationWindow } from 'src/pages/Conversations';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { GenericModalSet } from 'src/pages/Search/generic-modal-set';
import { SiteWindow } from 'src/pages/Sites/site';
import useNavigationStore from 'src/store/navigation';
import { useSelectedConversation } from 'src/store/selected-conversation';
import { useOnEsc } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';

const PreviewFrame = ({
  children,
  entityName,
  selectedRecord,
  sx,
}: {
  children: ReactNode;
  entityName: EntityNames;
  selectedRecord: AnySearchableEntity;
  sx?: SxProps<Theme>;
}) => (
  <Box height="100%" position="relative">
    <EntityWindowWrapper sx={sx}>{children}</EntityWindowWrapper>
    <GenericModalSet entityName={entityName} selectedRecord={selectedRecord} />
  </Box>
);

export const InboxPreview = () => {
  const theme = useTheme();
  const { navigate } = useNavigateRoute();
  const { recordId } = useRouteParams(UrlParams.recordId);

  // A refresh/deep-link lands straight on this route, skipping the /inbox
  // redirect that normally opens the pane — open it here once on mount. This
  // route is owned by neither pane in useRoutePane, so nothing recomputes
  // over it afterwards.
  useEffect(() => {
    useNavigationStore.getState().setActivePane('inbox');
  }, []);

  const { updateConversation } = useConversationUpdate();
  const onConversationUpdate = useCallback(
    async (conversation: Conversation) => {
      await updateConversation(conversation);
    },
    [updateConversation],
  );

  const parsed = recordId ? parseNotificationCompositeKey(recordId) : null;
  const entityName = getNotificationEntityName(parsed?.entity);

  const { selectedRecord, isLoading } = useSelectedRecord({
    recordId: parsed?.entityId,
    entityName: entityName ?? undefined,
  });

  const closeToInbox = useCallback(
    () => navigate(portalPaths.inbox.root(), { replace: true }),
    [navigate],
  );

  useOnEsc(closeToInbox);

  useEffect(() => {
    if (!recordId || !parsed || !entityName) {
      closeToInbox();
      return;
    }
    if (getNotificationOpenBehavior(entityName) === 'gridPeek') {
      const peekPath = getNotificationTargetPath({ entityName, entityId: parsed.entityId });
      if (peekPath) {
        navigate(peekPath, { replace: true });
      } else {
        closeToInbox();
      }
      return;
    }
    if (!isLoading && !selectedRecord) {
      closeToInbox();
    }
  }, [recordId, parsed, entityName, isLoading, selectedRecord, navigate, closeToInbox]);

  // SelectedEntitySync leaves the store alone on this preview route (it only syncs
  // the conversation full/allSelected/custom-view routes) — this effect owns the
  // store here instead, so conversation-scoped surfaces (e.g. Active Policies) see it.
  const conversationFriendlyId =
    entityName === EntityNames.Conversation
      ? ((selectedRecord as Conversation | null)?.friendlyId ?? null)
      : null;
  const { setSelectedConversationId } = useSelectedConversation();

  useEffect(() => {
    // while the next record loads, keep the previous conversation's context so
    // dependent surfaces (Active Policies) don't blink off on every arrow step
    if (!selectedRecord) return;
    setSelectedConversationId(conversationFriendlyId);
  }, [selectedRecord, conversationFriendlyId, setSelectedConversationId]);

  useEffect(() => () => setSelectedConversationId(null), [setSelectedConversationId]);

  if (
    !recordId ||
    !parsed ||
    !entityName ||
    getNotificationOpenBehavior(entityName) === 'gridPeek'
  ) {
    return null;
  }

  if (!selectedRecord) {
    return isLoading ? (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
      >
        <CircularProgress />
      </Box>
    ) : null;
  }

  if (entityName === EntityNames.Conversation) {
    return (
      <PreviewFrame entityName={entityName} selectedRecord={selectedRecord}>
        <ConversationWindow
          onUpdate={onConversationUpdate}
          conversation={selectedRecord as Conversation}
          onCloseConversation={closeToInbox}
          isSplitView
        />
      </PreviewFrame>
    );
  }

  if (entityName === EntityNames.Site) {
    const modeSwitcher = (
      <EntityOpenModeSwitcher
        openMode={RecordOpenMode.FullPage}
        data-testid="inbox-preview-open-full-page"
        onClick={() => navigate(portalPaths.site.full((selectedRecord as Address).addressId))}
      />
    );

    return (
      <PreviewFrame entityName={entityName} selectedRecord={selectedRecord}>
        <SiteWindow
          isSidePanelExpanded
          site={selectedRecord as Address}
          onCloseSite={closeToInbox}
          modeSwitcher={modeSwitcher}
        />
      </PreviewFrame>
    );
  }

  if (entityName === EntityNames.Client) {
    return (
      <PreviewFrame
        entityName={entityName}
        selectedRecord={selectedRecord}
        sx={{
          [theme.breakpoints.up('sm')]: {
            minWidth: '400px',
          },
        }}
      >
        <ClientWindow
          isSidePanelExpanded
          clientId={selectedRecord.id}
          onCloseClient={closeToInbox}
          modeSwitcher={
            <EntityOpenModeSwitcher
              openMode={RecordOpenMode.FullPage}
              data-testid="inbox-preview-open-full-page"
              onClick={() => navigate(portalPaths.client.full(selectedRecord.id))}
            />
          }
        />
      </PreviewFrame>
    );
  }

  return null;
};
